import { useSyncExternalStore } from 'react'

/** Minimal external store (no extra dependency) for app-wide UI state. */
export function createStore<T extends object>(initial: T) {
  let state = initial
  const listeners = new Set<() => void>()
  return {
    get: () => state,
    set(patch: Partial<T> | ((s: T) => Partial<T>)) {
      const p = typeof patch === 'function' ? patch(state) : patch
      state = { ...state, ...p }
      listeners.forEach((l) => l())
    },
    subscribe(l: () => void) {
      listeners.add(l)
      return () => {
        listeners.delete(l)
      }
    },
  }
}

export interface UIState {
  paletteOpen: boolean
  notificationsOpen: boolean
  shortcutsOpen: boolean
  /** Side help panel (web/src/help). */
  helpOpen: boolean
  /** Help entry to show instead of the current page's (deep link ?help=<key>). */
  helpKey?: string
  createTaskOpen: boolean
  createTaskDefaults?: { title?: string; entity_type?: string; entity_id?: string; description?: string }
  sidebarCollapsed: boolean
  mobileNavOpen: boolean
}

function initialCollapsed() {
  try {
    return localStorage.getItem('dv.sidebar') === 'collapsed'
  } catch {
    return false
  }
}

export const ui = createStore<UIState>({
  paletteOpen: false,
  notificationsOpen: false,
  shortcutsOpen: false,
  helpOpen: false,
  createTaskOpen: false,
  sidebarCollapsed: initialCollapsed(),
  mobileNavOpen: false,
})

export function useUI<S>(sel: (s: UIState) => S): S {
  return useSyncExternalStore(ui.subscribe, () => sel(ui.get()), () => sel(ui.get()))
}

export function toggleSidebar() {
  const v = !ui.get().sidebarCollapsed
  ui.set({ sidebarCollapsed: v })
  try {
    localStorage.setItem('dv.sidebar', v ? 'collapsed' : 'expanded')
  } catch {
    /* ignore */
  }
}

export function openCreateTask(defaults?: UIState['createTaskDefaults']) {
  ui.set({ createTaskOpen: true, createTaskDefaults: defaults })
}

export function toggleHelp(open?: boolean) {
  ui.set((s) => ({ helpOpen: open ?? !s.helpOpen }))
}
